import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Layers, Plus, Pencil, Trash2, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";

const SUGGEST = ["Term-1", "Term-2", "Term-3", "Half-Yearly", "Annual"];

export default function ExamGroupsTab({ session }) {
  const { user } = useAuth();
  const canManage = hasPerm(user, "manage_exams");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null); // {id, name}

  const load = useCallback(() => {
    if (!session) return;
    setLoading(true);
    api.get(`/exam-groups?session=${encodeURIComponent(session)}`).then((r) => setGroups(r.data)).finally(() => setLoading(false));
  }, [session]);
  useEffect(() => { load(); }, [load]);

  const add = async (val) => {
    const nm = (val ?? name).trim();
    if (!nm) { toast.error("Enter a group name"); return; }
    try {
      await api.post("/exam-groups", { name: nm, session, order: groups.length });
      setName(""); toast.success("Group added"); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    if (!editing?.name.trim()) { toast.error("Name required"); return; }
    try {
      await api.put(`/exam-groups/${editing.id}`, { name: editing.name.trim(), session, order: editing.order || 0 });
      setEditing(null); toast.success("Group updated"); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const remove = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"?`)) return;
    try { await api.delete(`/exam-groups/${g.id}`); toast.success("Group deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const existing = new Set(groups.map((g) => g.name.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="exam-groups-tab">
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <p className="text-sm text-slate-600">Create exam groups (terms) for session <b>{session || "—"}</b>. Exams are organised under these groups.</p>
        {canManage && (
          <>
            <div className="flex items-center gap-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New group name (e.g. Term-1)"
                onKeyDown={(e) => { if (e.key === "Enter") add(); }} className="max-w-xs" data-testid="group-name-input" />
              <Button onClick={() => add()} className="gap-1.5" data-testid="group-add-btn"><Plus size={16} />Add Group</Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {SUGGEST.filter((s) => !existing.has(s.toLowerCase())).map((s) => (
                <button key={s} onClick={() => add(s)} className="text-xs rounded-full border border-dashed border-slate-300 px-3 py-1 text-slate-500 hover:border-indigo-400 hover:text-indigo-600" data-testid={`group-suggest-${s}`}>+ {s}</button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? <div className="py-14 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          groups.length === 0 ? <div className="py-14 text-center text-slate-400"><Layers size={38} className="mx-auto mb-2 opacity-40" />No groups yet. Add your first term above.</div> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr><th className="px-4 py-2.5 text-left">#</th><th className="px-4 py-2.5 text-left">Group / Term</th><th className="px-4 py-2.5 text-right"></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groups.map((g, i) => (
                  <tr key={g.id} className="hover:bg-slate-50" data-testid={`group-row-${g.id}`}>
                    <td className="px-4 py-3 text-slate-400">{i + 1}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">
                      {editing?.id === g.id
                        ? <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); }} className="h-8 max-w-xs" data-testid="group-edit-input" />
                        : g.name}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canManage && (editing?.id === g.id ? (
                        <div className="flex gap-1 justify-end">
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" onClick={saveEdit} data-testid="group-save-edit"><Check size={16} /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(null)}><X size={16} /></Button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing({ id: g.id, name: g.name, order: g.order })} data-testid={`group-edit-${g.id}`}><Pencil size={15} /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => remove(g)} data-testid={`group-delete-${g.id}`}><Trash2 size={15} /></Button>
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}
